/**
 * spin-game-cart.js
 * Weiche Checkout-Sperre im Rebuy Smart Cart.
 *
 * Wenn der Nutzer seinen gewonnenen Artikel im Warenkorb hat,
 * aber noch nicht den Mindestbestellwert mit anderen Artikeln erreicht,
 * wird ein Banner angezeigt und jeder Weg zur Kasse gesperrt (Rebuy-Drawer,
 * Warenkorbseite, Links, Express-Checkout). Ein Klick darauf zeigt einen Hinweis
 * direkt am Button. Serverseitig stoppt zusätzlich die Checkout-Regel spin-validation.
 *
 * Gewinne sind nur am Tag der Ziehung einlösbar: Die Property _spin_win enthält
 * das Datum der Ziehung. Abgelaufene Gewinne werden beim Laden jeder Seite und
 * — falls eine Seite offen ist — um 00:00 Uhr deutscher Zeit aus dem Warenkorb entfernt.
 *
 * Laden: {{ 'spin-game-cart.js' | asset_url | script_tag }} in theme.liquid
 */

// =============================================================================
// Reine Logik-Funktionen (kein Browser-API, testbar in Node.js)
// =============================================================================

var SHOP_TIME_ZONE = 'Europe/Berlin';

/**
 * Heutiges Datum in deutscher Zeit (Zeitzone des Shops) als 'YYYY-MM-DD'.
 */
function shopDateToday(now) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: SHOP_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now || new Date());
}

/**
 * Millisekunden bis zum nächsten Tageswechsel (00:00 Uhr deutscher Zeit).
 */
function msUntilShopMidnight(now) {
  var date = now || new Date();
  var parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SHOP_TIME_ZONE, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  var get = function (type) { return Number(parts.find(function (p) { return p.type === type; }).value); };
  var elapsed = ((get('hour') * 60 + get('minute')) * 60 + get('second')) * 1000 + date.getMilliseconds();
  return 24 * 60 * 60 * 1000 - elapsed;
}

/**
 * Gewinn-Artikel, die nicht von heute sind. Gewinne aus der Zeit vor der
 * Tagesregel tragen '1' statt eines Datums und gelten ebenfalls als abgelaufen.
 */
function findExpiredWinItems(items, today) {
  if (!items) return [];
  return items.filter(function (item) {
    var drawDate = item.properties && item.properties['_spin_win'];
    return Boolean(drawDate) && drawDate !== today;
  });
}

/**
 * Findet den gewonnenen Warenkorb-Artikel.
 * Primär per _spin_win Property (eindeutig auch wenn dieselbe Variante
 * regulär nochmal gekauft wird), Fallback per variant_id.
 */
function findWonCartItem(items, wonVariantId) {
  if (!items || !wonVariantId) return null;
  return (
    items.find(function (item) {
      return Boolean(item.properties && item.properties['_spin_win']);
    }) ||
    items.find(function (item) {
      return String(item.variant_id) === String(wonVariantId);
    }) ||
    null
  );
}

/**
 * Berechnet den Gesamtbetrag aller Artikel AUSSER dem gewonnenen.
 * Filtert per item.key (eindeutig pro Zeile), nicht per variant_id —
 * damit ein regulär gekaufter gleicher Artikel korrekt mitzählt.
 */
function calculateOtherTotal(items, wonCartItem) {
  if (!items || !wonCartItem) return 0;
  var cents = items
    .filter(function (item) { return item.key !== wonCartItem.key; })
    .reduce(function (sum, item) { return sum + item.line_price; }, 0);
  return cents / 100;
}

/**
 * Liest den Mindestbestellwert aus:
 * 1. window.__spinGame.minOrderValue (nur auf der Spin-Game-Seite gesetzt)
 * 2. localStorage spin_game_min_order_value (von spin-game.js gesetzt)
 * 3. Fallback: 25
 */
function resolveMinOrderValue(spinGameConfig, localStorageValue) {
  return (spinGameConfig && spinGameConfig.minOrderValue) ||
    parseFloat(localStorageValue) ||
    25;
}

/**
 * Euro-Betrag im deutschen Format: 70 → '70', 23.5 → '23,50'.
 */
function formatEuro(value) {
  var rounded = Math.round(value * 100) / 100;
  return (rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2)).replace('.', ',');
}

/**
 * Hinweis am Kasse-Button, wenn der Mindestbestellwert noch nicht erreicht ist.
 */
function checkoutHintText(minOrderValue) {
  return 'Der Mindestbestellwert für das Blackweek-Geschenk liegt bei ' + formatEuro(minOrderValue) + ' Euro. ' +
    'Bitte passe deinen Warenkorb an.';
}

// Conditional export für Node.js / Tests
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    findWonCartItem, calculateOtherTotal, resolveMinOrderValue,
    shopDateToday, msUntilShopMidnight, findExpiredWinItems,
    formatEuro, checkoutHintText,
  };
}

// =============================================================================
// Browser-Runtime (IIFE) — wird nur im Browser ausgeführt
// =============================================================================
if (typeof window !== 'undefined') {
  (function () {
    'use strict';

    var LS_KEY    = 'spin_game_won_variant_id';
    var BANNER_ID = 'spin-game-cart-banner';

    // -------------------------------------------------------------------------
    // Rebuy-Selektoren — passen sich an falls Rebuy die Klassen ändert
    // -------------------------------------------------------------------------
    var CHECKOUT_BTN_SELECTORS = [
      '.rebuy-cart__checkout-button',
      '[data-rebuy-cart-checkout]',
      '.rebuy-cart__footer button[type="submit"]',
    ];

    // Alle Wege zur Kasse: Rebuy-Drawer, Warenkorbseite des Themes, Links auf /checkout
    var CHECKOUT_TRIGGER_SELECTORS = CHECKOUT_BTN_SELECTORS.concat([
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href="/checkout"]',
      'a[href^="/checkout?"]',
      'a[href$="/checkout"]',
    ]);

    // Express-Checkout im Warenkorb (Shop Pay, PayPal, Apple Pay …). Die Buttons liegen in
    // iframes bzw. Shadow DOM, dort lassen sich Klicks nicht abfangen — deshalb legt sich
    // per CSS eine Fläche darüber, deren Klick auf dem Container landet.
    // "Jetzt kaufen" auf der Produktseite bleibt frei: Es kauft nur das eine Produkt, ohne Geschenk.
    var EXPRESS_CHECKOUT_SELECTORS = [
      'shopify-accelerated-checkout-cart',
      '.additional-checkout-buttons',
      '[data-shopify="dynamic-checkout-cart"]',
    ];

    var FOOTER_SELECTORS = [
      '.rebuy-cart__footer',
      '[data-rebuy-cart-footer]',
      '.rebuy-cart__flyout-footer',
    ];

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    function findEl(selectors, root) {
      var base = root || document;
      for (var i = 0; i < selectors.length; i++) {
        var el = base.querySelector(selectors[i]);
        if (el) return el;
      }
      return null;
    }

    function getWonVariantId() {
      return localStorage.getItem(LS_KEY);
    }

    async function fetchCart() {
      try {
        var res = await fetch('/cart.js');
        return await res.json();
      } catch (_) {
        return null;
      }
    }

    // -------------------------------------------------------------------------
    // Banner erzeugen / aktualisieren / entfernen
    // -------------------------------------------------------------------------
    function upsertBanner(footer, remaining) {
      var banner = document.getElementById(BANNER_ID);
      if (!banner) {
        banner = document.createElement('div');
        banner.id = BANNER_ID;
        banner.style.cssText = [
          'background:#fff3cd',
          'border:1px solid #ffc107',
          'border-radius:6px',
          'color:#795600',
          'font-size:0.82rem',
          'font-weight:600',
          'line-height:1.4',
          'margin:0 0 10px',
          'padding:10px 12px',
          'text-align:center',
        ].join(';');
        footer.prepend(banner);
      }
      var text =
        'Füge noch ' + remaining.toFixed(2).replace('.', ',') + ' € an anderen Produkten hinzu,' +
        ' um deinen Gewinn gratis zu erhalten.';
      // Nur bei Änderung schreiben — jede Änderung weckt den MutationObserver
      if (banner.textContent !== text) banner.textContent = text;
    }

    function removeBanner() {
      var el = document.getElementById(BANNER_ID);
      if (el) el.remove();
    }

    // -------------------------------------------------------------------------
    // CSS-basierter Hinweis — Vue-proof, da <style> in <head> injiziert wird
    // und nicht durch Vue re-renders entfernt werden kann.
    // -------------------------------------------------------------------------
    var HINT_STYLE_ID   = 'spin-game-item-hint-style';
    var LOCK_STYLE_ID   = 'spin-game-qty-lock-style';
    var DIRECT_HINT_CLASS = 'spin-game-item-hint';

    function upsertItemHintCSS(cartItem, text, color) {
      var styleEl = document.getElementById(HINT_STYLE_ID);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = HINT_STYLE_ID;
        document.head.appendChild(styleEl);
      }
      if (!cartItem || !cartItem.variant_id || !text) {
        styleEl.textContent = '';
        removeDirectHints();
        return;
      }
      var safeText = text.replace(/"/g, '');
      var vid = cartItem.variant_id;
      styleEl.textContent =
        '.rebuy-cart__flyout-item-info:has(a[href*="' + vid + '"])::after,' +
        '[data-variant-id="' + vid + '"] .rebuy-cart__flyout-item-info::after,' +
        '[data-variant="' + vid + '"] .rebuy-cart__flyout-item-info::after {' +
        'content: "' + safeText + '" !important;' +
        'color: ' + (color || '#e65100') + ' !important;' +
        'font-size: 12px !important;' +
        'font-weight: 700 !important;' +
        'display: block !important;' +
        'margin-top: 5px !important;' +
        'font-family: Montserrat, -apple-system, sans-serif !important;' +
        'line-height: 1.3 !important;' +
        '}';

      injectDirectHint(cartItem.wonIndex, text, color);
    }

    function injectDirectHint(wonIndex, text, color) {
      var allInfos = document.querySelectorAll('.rebuy-cart__flyout-item-info');
      allInfos.forEach(function (info, idx) {
        var hint = info.querySelector('.' + DIRECT_HINT_CLASS);
        if (idx === wonIndex) {
          if (!hint) {
            hint = document.createElement('span');
            hint.className = DIRECT_HINT_CLASS;
            hint.style.cssText = 'color:' + (color || '#e65100') + ';font-size:12px;font-weight:700;display:block;margin-top:5px;font-family:Montserrat,sans-serif;';
            info.appendChild(hint);
          }
          if (hint.textContent !== text) hint.textContent = text;
        } else {
          if (hint) hint.remove();
        }
      });
    }

    function removeDirectHints() {
      document.querySelectorAll('.' + DIRECT_HINT_CLASS).forEach(function (el) { el.remove(); });
    }

    function removeItemHintCSS() {
      var el = document.getElementById(HINT_STYLE_ID);
      if (el) el.textContent = '';
      removeDirectHints();
    }

    // + Button des gewonnenen Artikels sperren (Vue-proof via CSS in <head>)
    function lockQuantityButton(cartItem) {
      var styleEl = document.getElementById(LOCK_STYLE_ID);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = LOCK_STYLE_ID;
        document.head.appendChild(styleEl);
      }
      if (!cartItem || !cartItem.variant_id) { styleEl.textContent = ''; return; }
      var vid = cartItem.variant_id;
      styleEl.textContent =
        '.rebuy-cart__flyout-item:has(a[href*="' + vid + '"]) .rebuy-cart__flyout-item-quantity button:last-child,' +
        '.rebuy-cart__flyout-item:has(a[href*="' + vid + '"]) [data-quantity-plus],' +
        '.rebuy-cart__flyout-item:has(a[href*="' + vid + '"]) button[aria-label*="ncrease"],' +
        '.rebuy-cart__flyout-item:has(a[href*="' + vid + '"]) button[aria-label*="rhöhen"] {' +
        'opacity: 0.25 !important;' +
        'pointer-events: none !important;' +
        'cursor: not-allowed !important;' +
        '}';
    }

    function unlockQuantityButton() {
      var el = document.getElementById(LOCK_STYLE_ID);
      if (el) el.textContent = '';
    }

    // Menge > 1 via AJAX auf 1 zurücksetzen
    async function enforceMaxQty(wonCartItem) {
      if (!wonCartItem || wonCartItem.quantity <= 1) return;
      try {
        await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: wonCartItem.key, quantity: 1 }),
        });
      } catch (_) {}
    }

    function removeDiscountHint() {
      removeItemHintCSS();
    }

    // -------------------------------------------------------------------------
    // Checkout sperren / entsperren
    // Die Buttons bleiben klickbar (kein disabled — sonst feuert kein Klick-Event).
    // Klicks werden abgefangen und zeigen stattdessen den Hinweis am Button.
    // -------------------------------------------------------------------------
    var EXPRESS_LOCK_STYLE_ID  = 'spin-game-express-lock-style';
    var CHECKOUT_HINT_ID       = 'spin-game-checkout-hint';
    var CHECKOUT_HINT_STYLE_ID = 'spin-game-checkout-hint-style';

    var checkoutBlock = null; // { minOrderValue } solange der Checkout gesperrt ist
    var hintAnchor    = null; // Element, an dem der Hinweis gerade steht

    function lockCheckout(btn) {
      if (!btn || btn.dataset.spinLocked) return;
      btn.dataset.spinLocked = '1';
      btn.setAttribute('aria-disabled', 'true');
      btn.style.opacity = '0.5';
      btn.style.cursor  = 'not-allowed';
    }

    function unlockCheckout(btn) {
      if (!btn || !btn.dataset.spinLocked) return;
      delete btn.dataset.spinLocked;
      btn.removeAttribute('aria-disabled');
      btn.style.opacity = '';
      btn.style.cursor  = '';
    }

    function lockExpressCheckout(locked) {
      var styleEl = document.getElementById(EXPRESS_LOCK_STYLE_ID);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = EXPRESS_LOCK_STYLE_ID;
        document.head.appendChild(styleEl);
      }
      var css = !locked ? '' :
        EXPRESS_CHECKOUT_SELECTORS.join(',') + ' {' +
        'position: relative !important;' +
        'opacity: 0.4 !important;' +
        'cursor: not-allowed !important;' +
        '}' +
        EXPRESS_CHECKOUT_SELECTORS.map(function (s) { return s + '::after'; }).join(',') + ' {' +
        'content: "" !important;' +
        'position: absolute !important;' +
        'inset: 0 !important;' +
        'z-index: 10 !important;' +
        '}' +
        EXPRESS_CHECKOUT_SELECTORS.map(function (s) { return s + ' > *'; }).join(',') + ' {' +
        'pointer-events: none !important;' +
        '}';
      if (styleEl.textContent !== css) styleEl.textContent = css;
    }

    function ensureCheckoutHintStyle() {
      if (document.getElementById(CHECKOUT_HINT_STYLE_ID)) return;
      var styleEl = document.createElement('style');
      styleEl.id = CHECKOUT_HINT_STYLE_ID;
      styleEl.textContent =
        '#' + CHECKOUT_HINT_ID + ' {' +
        'background: #fdecea; border: 1px solid #d32f2f; border-radius: 6px; color: #b71c1c;' +
        'box-sizing: border-box; flex-basis: 100%; width: 100%;' +
        // px statt rem: Das Theme setzt die Grundschrift auf 10px, 0.82rem wären nur ~8px
        'font-family: Montserrat, -apple-system, sans-serif; font-size: 14px; font-weight: 600; line-height: 1.4;' +
        'margin: 8px 0 0; padding: 10px 12px; text-align: center;' +
        '}' +
        '#' + CHECKOUT_HINT_ID + '.is-shaking { animation: spin-game-hint-shake 0.4s ease; }' +
        '@keyframes spin-game-hint-shake {' +
        '0%, 100% { transform: translateX(0); } 25% { transform: translateX(-6px); } 75% { transform: translateX(6px); }' +
        '}' +
        '@media (prefers-reduced-motion: reduce) { #' + CHECKOUT_HINT_ID + '.is-shaking { animation: none; } }';
      document.head.appendChild(styleEl);
    }

    // Ein einziges Hinweis-Element. Direkt referenziert statt per ID gesucht, weil Rebuy es
    // beim Re-Render aus dem DOM werfen kann und es dann wieder angehängt werden soll.
    var hintEl = null;

    function showCheckoutHint(anchor) {
      ensureCheckoutHintStyle();
      if (!hintEl) {
        hintEl = document.createElement('div');
        hintEl.id = CHECKOUT_HINT_ID;
        hintEl.setAttribute('role', 'alert');
      }
      var hint = hintEl;
      hint.textContent = checkoutHintText(checkoutBlock.minOrderValue);
      if (anchor.nextElementSibling !== hint) anchor.insertAdjacentElement('afterend', hint);
      hintAnchor = anchor;
      // Bei jedem weiteren Klick erneut wackeln
      hint.classList.remove('is-shaking');
      void hint.offsetWidth;
      hint.classList.add('is-shaking');
    }

    // Nach Cart-Änderungen: Betrag aktualisieren, nach Rebuy-Re-Render wieder anhängen,
    // bei erreichtem Mindestbestellwert entfernen
    function refreshCheckoutHint() {
      if (!hintAnchor) return;
      var hint = hintEl;
      if (!checkoutBlock) {
        if (hint) hint.remove();
        hintAnchor = null;
        return;
      }
      if (!hint) return;
      var text = checkoutHintText(checkoutBlock.minOrderValue);
      if (hint.textContent !== text) hint.textContent = text;
      if (!hint.isConnected) {
        var anchor = hintAnchor.isConnected ? hintAnchor : findEl(CHECKOUT_BTN_SELECTORS);
        if (anchor) {
          anchor.insertAdjacentElement('afterend', hint);
          hintAnchor = anchor;
        }
      }
    }

    function setCheckoutBlock(block) {
      checkoutBlock = block;
      document.querySelectorAll(CHECKOUT_TRIGGER_SELECTORS.join(',')).forEach(block ? lockCheckout : unlockCheckout);
      lockExpressCheckout(Boolean(block));
      refreshCheckoutHint();
    }

    // Klicks auf Kasse-Buttons abfangen — in der Capture-Phase am window, also bevor
    // Rebuy oder das Theme den Klick verarbeiten
    window.addEventListener('click', async function (e) {
      if (!checkoutBlock || !e.target || !e.target.closest) return;
      var trigger = e.target.closest(CHECKOUT_TRIGGER_SELECTORS.concat(EXPRESS_CHECKOUT_SELECTORS).join(','));
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      // Der Warenkorb kann sich gerade erst geändert haben (Auswertung läuft verzögert)
      await evaluate();
      if (checkoutBlock) {
        showCheckoutHint(trigger);
      } else {
        window.location.href = '/checkout';
      }
    }, true);

    // Absenden eines Formulars Richtung Kasse (z. B. Enter im Warenkorb-Formular)
    window.addEventListener('submit', function (e) {
      if (!checkoutBlock) return;
      var form = e.target;
      var submitter = e.submitter;
      var toCheckout = (submitter && submitter.name === 'checkout') ||
        /\/checkout(\?|$)/.test(form.getAttribute('action') || '');
      if (!toCheckout) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      showCheckoutHint(submitter || form);
    }, true);

    // -------------------------------------------------------------------------
    // Hauptlogik — läuft nach jeder Cart-Änderung
    // -------------------------------------------------------------------------
    async function evaluate() {
      // Wird bei jedem Aufruf neu gelesen — localStorage kann sich nach Seitenload ändern
      var MIN_ORDER_VALUE = resolveMinOrderValue(
        window.__spinGame,
        localStorage.getItem('spin_game_min_order_value')
      );
      var wonVariantId = getWonVariantId();
      if (!wonVariantId) {
        removeBanner();
        setCheckoutBlock(null);
        return;
      }

      var cart = await fetchCart();
      if (!cart || !cart.items) return;

      var wonCartItem = findWonCartItem(cart.items, wonVariantId);
      if (!wonCartItem) {
        removeBanner();
        removeDiscountHint();
        unlockQuantityButton();
        setCheckoutBlock(null);
        return;
      }

      // Menge auf 1 begrenzen
      lockQuantityButton(wonCartItem);
      await enforceMaxQty(wonCartItem);

      // Summe aller ANDEREN Artikel — per key filtern
      var otherTotal = calculateOtherTotal(cart.items, wonCartItem);

      var footer = findEl(FOOTER_SELECTORS);

      // Index für zielgenaues DOM-Injection (Rebuy = gleiche Reihenfolge)
      wonCartItem.wonIndex = cart.items.indexOf(wonCartItem);

      if (otherTotal < MIN_ORDER_VALUE) {
        var remaining = MIN_ORDER_VALUE - otherTotal;
        upsertItemHintCSS(wonCartItem, 'Lootbox Geschenk: GRATIS ab ' + MIN_ORDER_VALUE + ' \u20AC', '#e65100');
        if (footer) upsertBanner(footer, remaining);
        setCheckoutBlock({ minOrderValue: MIN_ORDER_VALUE });
      } else {
        removeItemHintCSS();
        removeBanner();
        setCheckoutBlock(null);
      }
    }

    // -------------------------------------------------------------------------
    // Abgelaufene Gewinne (nicht von heute) aus dem Warenkorb entfernen
    // -------------------------------------------------------------------------
    async function removeExpiredWins() {
      var cart = await fetchCart();
      if (!cart || !cart.items) return;
      var expired = findExpiredWinItems(cart.items, shopDateToday());
      if (!expired.length) return;
      for (var i = 0; i < expired.length; i++) {
        try {
          await fetch('/cart/change.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ id: expired[i].key, quantity: 0 }),
          });
        } catch (_) {}
      }
      localStorage.removeItem(LS_KEY);
      // Rebuy Smart Cart neu laden, damit der entfernte Artikel sofort verschwindet
      try {
        if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
          window.Rebuy.Cart.fetchCart();
        }
      } catch (_) {}
    }

    // Um 00:00 Uhr deutscher Zeit erneut prüfen, solange die Seite offen ist.
    // Feuert der Timer zu früh (z. B. Zeitumstellung), bleibt der Gewinn liegen, weil das Datum noch stimmt.
    function scheduleMidnightCleanup() {
      setTimeout(async function () {
        await removeExpiredWins();
        evaluate();
        scheduleMidnightCleanup();
      }, msUntilShopMidnight() + 1500);
    }

    // -------------------------------------------------------------------------
    // Rebuy Cart-Event + MutationObserver
    // -------------------------------------------------------------------------
    document.addEventListener('rebuy:cart:change', evaluate);
    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(evaluate, 200);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Beim Laden: erst abgelaufene Gewinne entfernen, dann prüfen (falls Cart schon offen)
    removeExpiredWins().then(evaluate);
    scheduleMidnightCleanup();

  })();
}
